// Plays an imported stereo WAV file with the left/right channels preserved exactly.
//
// Playback mechanism: HTMLAudioElement (via MediaElementAudioSourceNode) rather than
// decoding into an AudioBuffer. Reasons, documented per the README requirement:
//   1. HTMLMediaElement streams from a Blob URL and lets the browser's native decoder
//      handle arbitrarily large WAV files without holding the whole decoded PCM data
//      in memory (a Fender-length backing track WAV can be tens of megabytes decoded).
//   2. MediaElementAudioSourceNode preserves the source channel count through the
//      Web Audio graph (no implicit stereo->mono downmix as can happen with some
//      autonomous playback paths), and lets us apply the same explicit
//      splitter/merger routing used everywhere else in this app for consistency
//      and testability.
//   3. No Web Audio node in this path performs gain automation, filtering, or
//      dynamics processing — only an explicit identity passthrough (or explicit
//      left-only routing when requested for Tracks Only mode).

import { createExplicitStereoPassthrough, createLeftOnlyRoute, createRightOnlyRoute } from './stereoRouting';

export type FilePlaybackEvent =
  | { type: 'timeupdate'; currentTime: number; duration: number }
  | { type: 'ended' }
  | { type: 'error'; message: string }
  | { type: 'playing' }
  | { type: 'pause' };

export class FilePlaybackEngine {
  private audioContext: AudioContext;
  private mediaElement: HTMLAudioElement | null = null;
  private sourceNode: MediaElementAudioSourceNode | null = null;
  private objectUrl: string | null = null;
  private listeners = new Set<(event: FilePlaybackEvent) => void>();
  private boundHandlers: Record<string, () => void> = {};

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
  }

  subscribe(listener: (event: FilePlaybackEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: FilePlaybackEvent): void {
    for (const listener of this.listeners) listener(event);
  }

  /** Loads a blob and prepares it for playback. Does not start playback.
   * `outputSide` selects the routing: 'stereo' is a plain passthrough
   * (whatever the file's own L/R channels are), 'left'/'right' sum both of
   * the file's own channels onto just that output — used for Tracks Only's
   * own outputSide option (any of the three is a legitimate choice — plain
   * stereo included, e.g. someone just practicing/singing along with no
   * PA/in-ear split to worry about), and for a Tracks + Click song freeing
   * up one side for its synthetic click. */
  async load(blob: Blob, outputSide: 'stereo' | 'left' | 'right' = 'stereo'): Promise<void> {
    this.teardown();

    const url = URL.createObjectURL(blob);
    this.objectUrl = url;

    const element = new Audio();
    element.preload = 'auto';
    element.src = url;
    // Never let the element or platform decide to loop/continue automatically.
    element.loop = false;
    element.autoplay = false;
    this.mediaElement = element;

    const onTimeUpdate = () => {
      this.emit({ type: 'timeupdate', currentTime: element.currentTime, duration: element.duration || 0 });
    };
    const onEnded = () => this.emit({ type: 'ended' });
    const onError = () => {
      const mediaError = element.error;
      this.emit({ type: 'error', message: describeMediaError(mediaError) });
    };
    const onPlaying = () => this.emit({ type: 'playing' });
    const onPause = () => this.emit({ type: 'pause' });

    element.addEventListener('timeupdate', onTimeUpdate);
    element.addEventListener('ended', onEnded);
    element.addEventListener('error', onError);
    element.addEventListener('playing', onPlaying);
    element.addEventListener('pause', onPause);

    this.boundHandlers = {
      timeupdate: onTimeUpdate,
      ended: onEnded,
      error: onError,
      playing: onPlaying,
      pause: onPause,
    };

    await new Promise<void>((resolve, reject) => {
      const onLoaded = () => {
        cleanup();
        resolve();
      };
      const onLoadError = () => {
        cleanup();
        reject(new Error(describeMediaError(element.error)));
      };
      const cleanup = () => {
        element.removeEventListener('loadedmetadata', onLoaded);
        element.removeEventListener('error', onLoadError);
      };
      element.addEventListener('loadedmetadata', onLoaded, { once: true });
      element.addEventListener('error', onLoadError, { once: true });
    });

    // Route through the Web Audio graph with explicit, testable channel wiring.
    const sourceNode = this.audioContext.createMediaElementSource(element);
    this.sourceNode = sourceNode;

    const route =
      outputSide === 'left'
        ? createLeftOnlyRoute(this.audioContext)
        : outputSide === 'right'
          ? createRightOnlyRoute(this.audioContext)
          : createExplicitStereoPassthrough(this.audioContext);

    sourceNode.connect(route.input);
    route.output.connect(this.audioContext.destination);
  }

  async play(): Promise<void> {
    if (!this.mediaElement) throw new Error('No song loaded.');
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    await this.mediaElement.play();
  }

  pause(): void {
    this.mediaElement?.pause();
  }

  /** Stops and rewinds to the beginning, per the "stopped song returns to its beginning" rule. */
  stop(): void {
    if (!this.mediaElement) return;
    this.mediaElement.pause();
    this.mediaElement.currentTime = 0;
  }

  /** Moves the playhead without affecting play/pause state. Clamped to
   * [0, duration] since HTMLMediaElement.currentTime silently clamps on some
   * browsers but not reliably all — being explicit here is cheap insurance. */
  seek(timeSeconds: number): void {
    if (!this.mediaElement) return;
    const duration = this.mediaElement.duration || 0;
    this.mediaElement.currentTime = Math.max(0, Math.min(duration, timeSeconds));
  }

  get currentTime(): number {
    return this.mediaElement?.currentTime ?? 0;
  }

  get duration(): number {
    return this.mediaElement?.duration ?? 0;
  }

  get isPaused(): boolean {
    return this.mediaElement?.paused ?? true;
  }

  /** Changes playback speed while preserving pitch — browser-native time-
   * stretch via HTMLMediaElement.playbackRate + preservesPitch, no DSP code
   * of our own needed. Used by Performance mode's practice-speed control so
   * a song can be slowed down to learn a hard part, or sped up to drill it
   * faster than performance tempo, without the audio's pitch shifting
   * either way the way a naive sample-rate change would. HTMLMediaElement
   * keeps `currentTime` in the file's own original timeline regardless of
   * rate, so markers/loop boundaries/seek() all stay correct with no
   * changes anywhere else. preservesPitch is set on every call (not just
   * once) since it has no effect until a rate actually differs from 1 on
   * some browsers, and costs nothing to re-assert. */
  setPlaybackRate(rate: number): void {
    if (!this.mediaElement) return;
    this.mediaElement.playbackRate = rate;
    const el = this.mediaElement as HTMLAudioElement & { mozPreservesPitch?: boolean; webkitPreservesPitch?: boolean };
    this.mediaElement.preservesPitch = true;
    el.mozPreservesPitch = true; // older Firefox
    el.webkitPreservesPitch = true; // older Safari
  }

  /** Fully releases the media element and Web Audio nodes. */
  teardown(): void {
    if (this.mediaElement) {
      this.mediaElement.pause();
      for (const [event, handler] of Object.entries(this.boundHandlers)) {
        this.mediaElement.removeEventListener(event, handler);
      }
      this.mediaElement.removeAttribute('src');
      this.mediaElement.load();
    }
    this.sourceNode?.disconnect();
    this.sourceNode = null;
    this.mediaElement = null;
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    this.boundHandlers = {};
  }
}

function describeMediaError(error: MediaError | null): string {
  if (!error) return 'Unknown playback error.';
  switch (error.code) {
    case MediaError.MEDIA_ERR_ABORTED:
      return 'Playback was aborted.';
    case MediaError.MEDIA_ERR_NETWORK:
      return 'A network error prevented loading the audio.';
    case MediaError.MEDIA_ERR_DECODE:
      return 'The audio file appears to be corrupted and could not be decoded.';
    case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
      return 'This audio format is not supported by the browser.';
    default:
      return 'Unknown playback error.';
  }
}
